import React, { Component } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Box,
    Grid,
    Switch,
} from "@mui/material";
import { loadFromLocalStorage } from "../../Common/Validation/LocalStorage";
import { SlideUpTransition } from "../../Component/Reusables/Transitions";
import { PaymentMethodResponse } from "./Interface";
import InputTextField from "../../Component/Reusables/InputTextField";
import ExchanageRateService from "../../Services/ExchangeRateService";
import { ShowAlert } from "../../Component/ShowAlert";

interface UpdatePaymentMethodProps {
    open: boolean;
    accessToken: string;
    onClose?: (state: boolean, action: string) => void;
    refreshData: () => void;
    paymentMethodData: {
        paymentMethods: PaymentMethodResponse[] | null;
    };
    updatePaymentMethodCountry: string;
}

interface UpdatePaymentMethodState {
    paymentMethods: PaymentMethodResponse[];
    accessToken: string;
    updatePaymentMethodCountry: string;
    loading: boolean;
}

class UpdatePaymentMethod extends Component<UpdatePaymentMethodProps, UpdatePaymentMethodState> {
    constructor(props: UpdatePaymentMethodProps) {
        super(props);
        this.state = {
            paymentMethods:
                props.paymentMethodData.paymentMethods?.map(method => ({
                    ...method,
                    isModified: false
                })) || [],
            accessToken: props.accessToken,
            updatePaymentMethodCountry: props.updatePaymentMethodCountry,
            loading: false
        };
    }

    async componentDidMount() {
        const token = await loadFromLocalStorage("accessToken");
        if (token) {
            this.setState({ accessToken: token.toString() });
        } else {
            localStorage.clear();
            window.location.href = "/";
        }
    }

    handleToggle = (index: number, checked: boolean) => {
        const paymentMethods = [...this.state.paymentMethods];
        paymentMethods[index] = {
            ...paymentMethods[index],
            isDisable: checked ? 0 : 1,
            isModified: true
        };
        this.setState({ paymentMethods });
    };

    handleInputChange = (index: number, value: string) => {
        const paymentMethods = [...this.state.paymentMethods];
        paymentMethods[index] = {
            ...paymentMethods[index],
            receiverLimit: value === "" ? 0 : Number(value),
            isModified: true
        };
        this.setState({ paymentMethods });
    };

    private handleSubmit = async (action: string) => {
        if (action !== "ok") {
            this.props.onClose?.(false, action);
            return;
        }

        const modifiedMethods = this.state.paymentMethods.filter(
            method => method.isModified
        );

        if (modifiedMethods.length === 0) {
            ShowAlert("Info", "No changes to save.", "info");
            return;
        }

        this.setState({ loading: true });

        const payload = modifiedMethods.map(method => ({
            paymentMethodId: String(method.paymentMethodId),
            isDisable: String(method.isDisable),
            receiverLimit: String(method.receiverLimit ?? 0)
        }));

        try {
            const response = await ExchanageRateService.updatePaymentMethod(
                this.state.accessToken,
                payload
            );

            if (response.ok) {
                this.setState({ loading: false });
                this.props.onClose?.(true, action);
                ShowAlert("Success", "Payment method updated successfully.", "success")
                    .then(() => { this.props.refreshData() });
            }
        } catch (error) {
            console.error("Error updating payment methods:", error);
            this.setState({ loading: false });
            ShowAlert("Error", "Failed to update payment methods.", "error");
        }
    };

    render() {
        const { open, onClose } = this.props;
        const { paymentMethods, loading, updatePaymentMethodCountry } = this.state;

        return (
            <Dialog
                open={open}
                onClose={() => onClose?.(false, "cancel")}
                fullWidth
                maxWidth="sm"
                TransitionComponent={SlideUpTransition}
                keepMounted
            >
                <DialogTitle>Payment Methods ({updatePaymentMethodCountry})</DialogTitle>

                <DialogContent>
                    <Box sx={{
                        border: "1px solid #ddd",
                        borderRadius: 1,
                        padding: 2
                    }}
                    >
                        {paymentMethods.map((method, index) => (
                            <Grid container
                                spacing={2} alignItems="center"
                                key={method.paymentMethodId}
                                mb={1}
                            >
                                <Grid item xs={3}>
                                    <span>{method.name}</span>
                                </Grid>

                                <Grid item xs={3}>
                                    <Switch
                                        checked={method.isDisable === 0}
                                        onChange={(e) =>
                                            this.handleToggle(index, e.target.checked)
                                        }
                                        color="secondary"
                                    />
                                    <strong>
                                        {method.isDisable === 0
                                            ? "Enabled"
                                            : "Disabled"}
                                    </strong>
                                </Grid>

                                <Grid item xs={6}>
                                    <InputTextField
                                        placeholder="Enter receiver limit"
                                        inputType="number"
                                        startAdornment={method.currencySymbol}
                                        disabled={method.isDisable !== 0}
                                        value={method.receiverLimit?.toString() ?? "0"}
                                        onChange={(value: string) =>
                                            this.handleInputChange(index, value)
                                        }
                                        inputProps={{
                                            style: { textAlign: 'right' },
                                            inputMode: "decimal", 
                                        }}
                                    />
                                </Grid>
                            </Grid>
                        ))}
                    </Box>
                </DialogContent>

                <DialogActions>
                    <Button onClick={() => onClose?.(false, "cancel")}
                    >
                        Cancel
                    </Button>
                    <Button
                        color="secondary"
                        variant="contained"
                        onClick={() => this.handleSubmit("ok")}
                        disabled={loading}
                    >
                        Update
                    </Button>
                </DialogActions>
            </Dialog>
        );
    }
}

export default UpdatePaymentMethod;
